import { useEffect, useRef } from "react";
import api from "../../services/api";

/**
 * useActivityPulse
 *
 * Tracks user activity (mouse, keyboard, touch, click) and sends a heartbeat
 * to POST /auth/activity-pulse every PULSE_INTERVAL_MS while authenticated.
 *
 * Each pulse carries:
 *   - actionsCount: number of distinct interactions since the last pulse
 *   - closing: true only when the tab is being unloaded (beforeunload)
 *
 * On beforeunload the pulse is sent via navigator.sendBeacon so it fires
 * even as the page tears down, sealing the session with accurate data.
 *
 * This is the only way activeSeconds / idleSeconds / actionsCount get
 * populated in UserLoginSession — without this hook all activity metrics
 * in the UserActivityDrawer show 0.
 */

const PULSE_INTERVAL_MS = 60_000; // send every 60 seconds
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const useActivityPulse = (isAuth) => {
  const actionsRef      = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const intervalRef     = useRef(null);

  useEffect(() => {
    if (!isAuth) return;

    // ── Activity listeners ────────────────────────────────────────────────
    const trackAction = () => {
      actionsRef.current += 1;
      lastActivityRef.current = Date.now();
    };

    const opts = { passive: true };
    window.addEventListener("mousemove",  trackAction, opts);
    window.addEventListener("keydown",    trackAction, opts);
    window.addEventListener("click",      trackAction, opts);
    window.addEventListener("touchstart", trackAction, opts);
    window.addEventListener("scroll",     trackAction, opts);

    // ── Periodic heartbeat ────────────────────────────────────────────────
    const sendPulse = async () => {
      const actions = actionsRef.current;
      actionsRef.current = 0;

      // Skip if user has been completely idle and no actions accumulated
      const idleMs = Date.now() - lastActivityRef.current;
      if (actions === 0 && idleMs > PULSE_INTERVAL_MS * 2) return;

      try {
        await api.post("/auth/activity-pulse", { actionsCount: actions });
      } catch {
        // Silently ignore — network blip should not disrupt the user
      }
    };

    intervalRef.current = setInterval(sendPulse, PULSE_INTERVAL_MS);

    // ── Tab / browser close ───────────────────────────────────────────────
    const handleBeforeUnload = () => {
      // navigator.sendBeacon works even while the page is unloading.
      // We include closing:true so the backend seals the session immediately.
      const payload = JSON.stringify({
        actionsCount: actionsRef.current,
        closing: true,
      });
      const url = `${API_BASE}/api/auth/activity-pulse`;
      try {
        navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
      } catch {
        // sendBeacon not available (old browser) — best effort only
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // ── Cleanup ───────────────────────────────────────────────────────────
    return () => {
      window.removeEventListener("mousemove",     trackAction);
      window.removeEventListener("keydown",       trackAction);
      window.removeEventListener("click",         trackAction);
      window.removeEventListener("touchstart",    trackAction);
      window.removeEventListener("scroll",        trackAction);
      window.removeEventListener("beforeunload",  handleBeforeUnload);
      clearInterval(intervalRef.current);
    };
  }, [isAuth]);
};

export default useActivityPulse;
